/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { Checkbox } from "@eds/react/Checkbox";
import { useCallback, useState } from "react";
import type { FC } from "react";

import { CosmosButton, CosmosTextVariantEnum, EdsIcon } from "@components/elements/atoms";
import { css } from "@panda/css";
import { Box, Flex, HStack, VStack } from "@panda/jsx";

import { EulaAgreementError } from "./EulaAgreementError.atom";
import { CosmosDesc } from "@components/elements/molecules/title-desc-stack";
import { useLocale, useMessages } from "@epic-mw/i18n";
import { useIsOptIn, useUpdateOptIn } from "@cosmos/next-hooks";

export interface EulaAgreementFooterProps {
	acceptedContent?: string | null;
	acceptOptionLabel?: string | null;
	acceptButtonLabel?: string | null;
	mustAcceptWarning?: string | null;
}

const agreementFormStyles = css({ color: "eds.text.critical" });
export interface EulaAgreementFooterExtraProps {
	accepted?: boolean;
	isLoading?: boolean;
	onSubmit: () => void;
	error?: string | null;
	title?: string | null;
	hideOptIn?: boolean;
}

export const AgreementFormGroup = ({
	checked,
	handleChange,
	acceptOptionLabel = "I have read and agree with Agreement",
	showError,
}: {
	checked: boolean;
	handleChange: () => void;
	acceptOptionLabel?: string | null;
	showError?: boolean;
}): React.JSX.Element => {
	return (
		<Flex>
			<Checkbox className="accept" checked={checked} onChange={handleChange} label={acceptOptionLabel} />
			{showError ? <span className={agreementFormStyles}>*</span> : null}
		</Flex>
	);
};

const optInFormStyles = css({
	gap: "eds.dimension.10",
	"& span.cosmos-rich-text": {
		paddingInlineStart: 0,
	},
	mdDown: {
		flexDirection: "column",
		alignItems: "flex-start",
		gap: "eds.dimension.2",
		"& span.cosmos-rich-text": {
			paddingInlineStart: "eds.dimension.32",
		},
	},
	md: {
		alignItems: "center",
	},
});

export const OptInFormGroup = ({
	checked,
	handleChange,
}: {
	checked: boolean;
	handleChange: () => void;
}): React.JSX.Element => {
	const messages = useMessages();
	const locale = useLocale();
	const privacyPolicyLabel = messages["epic.cosmos.privacypolicy"] || "";
	const privacyPolicyHtml = `<a href="https://www.epicgames.com/site/privacypolicy?lang=${encodeURIComponent(locale)}">${privacyPolicyLabel}</a>`;

	return (
		<Flex className={optInFormStyles}>
			<Checkbox
				className="optin"
				checked={checked}
				onChange={handleChange}
				label={messages["epic.cosmos.globaloptin.checkbox"]}
			/>
			<CosmosDesc description={{ html: privacyPolicyHtml, variant: "uiSM" }} />
		</Flex>
	);
};

type OptInGroupCheckProps = {
	submitOptIn: boolean;
	optInCheckbox: boolean;
	handleOptInChange: () => void;
};

const OptInGroupCheck = ({
	submitOptIn,
	handleOptInChange,
	optInCheckbox,
}: OptInGroupCheckProps): React.JSX.Element | null => {
	const { isOptInSuccess } = useUpdateOptIn(submitOptIn);
	const { hasOptIn } = useIsOptIn();

	return !hasOptIn && !isOptInSuccess ? (
		<OptInFormGroup handleChange={handleOptInChange} checked={optInCheckbox} />
	) : null;
};

const agreementFoolterWrapperStyles = css({
	smDown: { flexDirection: "column", alignItems: "stretch" },
});
const agreementFoolterStackStyles = css({ gap: "eds.dimension.12", mdDown: { gap: "eds.dimension.18" } });

const acceptButtonStyles = css({
	smDown: {
		maxWidth: "100%",
		minHeight: "token(sizes.eds.dimension.40)",
		paddingBlock: "token(sizes.eds.dimension.8)",
		paddingInline: "token(sizes.eds.dimension.16)",
	},
});

export const EulaAgreementFooter: FC<EulaAgreementFooterProps & EulaAgreementFooterExtraProps> = ({
	isLoading,
	accepted,
	onSubmit,
	error,
	acceptedContent,
	acceptOptionLabel,
	acceptButtonLabel,
	mustAcceptWarning,
	title,
	hideOptIn,
}): React.JSX.Element => {
	const [submitOptIn, setSubmitOptIn] = useState(false);

	const [eulaCheckbox, setEulaCheckbox] = useState(false);
	const [optInCheckbox, setOptInCheckbox] = useState(false);
	const [showError, setShowError] = useState(false);

	const handleChange = useCallback(() => {
		setEulaCheckbox(!eulaCheckbox);
		setShowError(!showError && eulaCheckbox);
	}, [eulaCheckbox, showError, setShowError, setEulaCheckbox]);

	const handleOptInChange = useCallback(() => {
		setOptInCheckbox(!optInCheckbox);
	}, [optInCheckbox, setOptInCheckbox]);

	const acceptCallbackValidator = useCallback(() => {
		if (!isLoading) {
			if (eulaCheckbox) {
				onSubmit();
			}
			if (optInCheckbox) {
				setSubmitOptIn(true);
			}
		}
	}, [eulaCheckbox, isLoading, onSubmit, optInCheckbox, setSubmitOptIn]);

	if (accepted) {
		return (
			<Box w="full">
				<HStack
					justifyContent="center"
					w="full"
					rowGap="eds.dimension.16"
					columnGap="eds.dimension.32"
					className={css({ "& svg": { flexShrink: 0 } })}
				>
					<EdsIcon icon="CheckCircleFilled" size="md" color="success" />
					<CosmosDesc
						description={{
							html: acceptedContent || `You accepted the ${title}`,
							variant: CosmosTextVariantEnum.paragraphMD,
							as: "div",
							color: "eds.text.contrast",
						}}
						className={css({ overflowWrap: "anywhere" })}
					/>
				</HStack>
			</Box>
		);
	}

	return (
		<Box w="full">
			<HStack justifyContent="space-between" w="full" gap="eds.dimension.32" className={agreementFoolterWrapperStyles}>
				<VStack alignItems="flex-start" className={agreementFoolterStackStyles}>
					{!hideOptIn ? (
						<OptInGroupCheck
							submitOptIn={submitOptIn}
							handleOptInChange={handleOptInChange}
							optInCheckbox={optInCheckbox}
						/>
					) : null}
					<AgreementFormGroup
						handleChange={handleChange}
						checked={eulaCheckbox}
						acceptOptionLabel={acceptOptionLabel || `I have read and agree with the ${title}`}
						showError={showError}
					/>
				</VStack>
				<CosmosButton
					variant={!eulaCheckbox ? "secondary" : "primary"}
					disabled={!eulaCheckbox}
					onClick={acceptCallbackValidator}
					className={acceptButtonStyles}
				>
					{acceptButtonLabel || "Accept"}
				</CosmosButton>
			</HStack>
			<EulaAgreementError
				error={error}
				mustAgreeWarning={mustAcceptWarning}
				showError={showError}
				eulaCheckbox={eulaCheckbox}
			/>
		</Box>
	);
};
